/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { Edit2, X } from 'lucide-react';
import React, { FC, useRef } from 'react';

import { Button } from '@/components/atoms/Button';
import { cn } from '@/lib/utils';

export const VariablesPopover: FC<{
    variables: { [key: string]: string };
    editVariables?: (action: 'add' | 'remove', payload: { [key: string]: string }) => void;
    updateInput?: (string: string) => void;
}> = ({ variables, editVariables, updateInput }) => {
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;
        const name = (form[0] as HTMLInputElement).value;
        const value = (form[1] as HTMLInputElement).value;
        if (name && value) {
            editVariables?.('add', { [name]: value });
            form.reset();
        }
    };

    const formRef = useRef<HTMLFormElement>(null);

    const rewriteVariable = (key: string) => {
        const value = variables[key];
        editVariables?.('remove', { [key]: value });
        const form = formRef.current;
        if (form) {
            (form[0] as HTMLInputElement).value = key;
            (form[1] as HTMLInputElement).value = value;
        }
    };

    return (
        <div>
            <div className="px-2 py-3 flex items-center text-slate-700 gap-1 border-b font-medium leading-6">
                <span className="rounded h-[24px] px-0.5 bg-purple-100 text-purple-900 flex items-center justify-center">
                    <span className="text-detail">{'{{var}}'}</span>
                </span>
                Variables
            </div>
            <div className="py-1 px-2">
                {editVariables && updateInput && (
                    <form className="border-b gap-y-2 flex flex-col" onSubmit={handleSubmit} ref={formRef}>
                        <div className="font-medium leading-6">Create Variable</div>
                        <input
                            className="border placeholder:text-slate-400 rounded-md py-1 px-3 text-sm"
                            placeholder="Name"
                        />
                        <input
                            className="border placeholder:text-slate-400 rounded-md py-1 px-3 text-sm"
                            placeholder="Value (string or function)"
                        />
                        <Button color="purple" size="small" type="submit" theme="hollow" className="ms-auto block mb-5">
                            Create
                        </Button>
                    </form>
                )}
                <div className={cn(editVariables && updateInput && 'pb-8')}>
                    {editVariables && updateInput && <div className="font-medium leading-6">Story Variables</div>}
                    <div className="max-h-[15vh] overflow-y-auto">
                        {Object.entries(variables).length > 0 ? (
                            Object.entries(variables).map(([key, value]) => (
                                <div className="flex items-center justify-between max-w-full" key={key}>
                                    <button
                                        aria-label={`Insert variable ${key} into input`}
                                        onClick={() => {
                                            updateInput?.(key);
                                        }}
                                        className="hover:font-medium hover:text-slate-700 inline items-center leading-8 text-left text-sm truncate  w-full"
                                    >
                                        <span className="w-[35%] truncate pr-1">{`{{${key}}}`}</span>
                                        <span className="text-slate-400">{value}</span>
                                    </button>
                                    {editVariables && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => {
                                                    rewriteVariable(key);
                                                }}
                                                aria-label={`Edit variable ${key}`}
                                                className="text-slate-400"
                                            >
                                                <Edit2 className="text-slate-400" width="16px" height="16px" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    editVariables('remove', { [key]: value });
                                                }}
                                                aria-label={`Remove variable ${key}`}
                                                className="text-slate-400"
                                            >
                                                <X className="text-slate-400" width="16px" height="16px" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="text-slate-400 text-sm">No variables created yet</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
